<template>
  <div class="content-wrapper">
    <!-- Controls Toolbar -->
    <div class="controls" style="position: sticky; top: 0; z-index: 1000;">
      <div class="left-controls">
        <!-- Refresh button -->
        <button class="refresh-button" @click="refreshData" title="Refresh">
          <span class="material-icons">refresh</span>
        </button>
        <button class="refresh-button" @click="downloadData" title="Download" :disabled="downloading">
          <span class="material-icons" :class="{ 'loading': downloading }">download</span>
        </button>

        <!-- Value radio buttons -->
        <form action="#" class="radio-group">
          <label class="radio-label">
            <input name="value_group" type="radio" v-model="valueType" value="absolute" />
            <span :title="$t('dashboard.absolutevalues')">{{ $t('dashboard.absolutevalues') }}</span>
          </label>
          <label class="radio-label">
            <input name="value_group" type="radio" v-model="valueType" value="relative" />
            <span :title="$t('dashboard.relativevalues')">{{ $t('dashboard.relativevalues') }}</span>
          </label>
        </form>

        <!-- Value checkbox -->
        <label class="checkbox-label">
          <input type="checkbox" v-model="showNonZeroOnly" />
          <span :title="$t('dashboard.shownonzero')">{{ $t('dashboard.shownonzero') }}</span>
        </label>
      </div>
      <div class="right-controls">
        <div class="autocomplete-container">
          <input
            type="text"
            v-model="searchQuery"
            :placeholder="$t('views.navbar.searchobjects')"
            class="autocomplete-input select-s2 browser-default small-contents"
            @focus="showSuggestions = true"
            @input="onInput"
            @keydown.up="moveSelection(-1)"
            @keydown.down="moveSelection(1)"
            @keydown.enter="selectHighlightedSuggestion"
            @keydown.esc="clearSearchClick"
          >
          <ul class="suggestions-list" v-if="showSuggestions && filteredSuggestions.length > 0">
            <li
              v-for="(suggestion, index) in filteredSuggestions"
              :key="suggestion"
              class="suggestion-item"
              :class="{ 'highlighted': index === selectedIndex }"
              @click="selectSuggestion(suggestion)"
            >
              {{ suggestion }}
            </li>
          </ul>
        </div>
        <button class="refresh-button" @click="clearSearchClick" title="Clear Search">
          <span class="material-icons">clear</span>
        </button>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="error-message">
      <span class="error-icon">⚠️</span> {{ errorMessage }}
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="spinner-container">
      <div class="spinner"></div>
    </div>

    <!-- No Data Message -->
    <div v-else-if="!errorMessage && (!filteredData || filteredData.length === 0)">
      {{ $t('dashboard.nodataavailable')}}
    </div>

    <!-- Table with Pagination -->
    <div v-else class="table-container">
      <div class="table-wrapper">
        <table class="incidents-table">
          <thead>
            <tr>
              <th style="text-align: left;" @click="sort('LocationName')">
                {{ $t('views.alarmlist.location')}}
                <span v-if="sortKey === 'LocationName' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('AbsoluteTotal')">
                {{ $t('dashboard.total')}}
                <span v-if="sortKey === 'AbsoluteTotal' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th v-for="(date, index) in last10Dates" :key="'date-' + index" @click="sort(date)">
                {{ formatDate(date) }}
                <span v-if="sortKey === date && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="location in paginatedData" :key="location.LocationId">
              <td style="text-align: left;" :title="location.LocationName">
                {{ location.LocationName }}
              </td>
              <td :class="getColor(location.displayTotal, maxTotal)">
                {{ formatValue(location.displayTotal) }}
              </td>
              <td v-for="(val, index) in location.last10Days" :key="'val-' + index"
                  :class="getColor(val.displayAlarms, maxErrorsPerDate[index])">
                {{ formatValue(val.displayAlarms) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Controls -->
      <div v-if="paginatedData.length > 0" class="pagination">
        <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn">
          <span class="material-icons">navigate_before</span>
        </button>
        <span>{{ $t('dashboard.page')}} {{ currentPage }} {{ $t('dashboard.of')}} {{ totalPages }}</span>
        <button @click="nextPage" :disabled="currentPage >= totalPages" class="pagination-btn">
          <span class="material-icons">navigate_next</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import AuthenticationService from '@/services/AuthenticationService'
import DashboardService from '@/services/DashboardService'

export default {
  name: 'Incidents',
  components: {},
  props: ['companyId', 'railObjects'],
  emits: ['updating'],
  data () {
    return {
      loading: false,
      currentUser: AuthenticationService.getUser(),
      errorMessage: null,
      incidents: [],
      last10Dates: [],
      processedData: [],
      currentPage: 1,
      rowsPerPage: 10,
      sortKey: '',
      sortDirection: 'default',
      searchQuery: '',
      searchItems: {},
      selectedSearchItem: '',
      showSuggestions: false,
      selectedIndex: -1,
      isSelecting: false,
      valueType: 'absolute',
      showNonZeroOnly: false,
      downloading: false
    }
  },

  mounted () {
    window.addEventListener('resize', this.updateRowsPerPage)
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.updateRowsPerPage)
    document.removeEventListener('click', this.handleClickOutside)
  },
  computed: {
    filteredSuggestions () {
      if (!this.searchQuery) return Object.keys(this.searchItems)
      const query = this.searchQuery.toLowerCase()
      return Object.keys(this.searchItems).filter(item =>
        item.toLowerCase().includes(query)
      )
    },

    filteredData () {
      let filteredData = [...this.processedData]

      // Apply filtering based on selected search item
      if (this.selectedSearchItem && this.searchItems[this.selectedSearchItem]) {
        const filteredLocationIds = this.searchItems[this.selectedSearchItem]
        filteredData = filteredData.filter(item =>
          filteredLocationIds.includes(item.LocationId)
        )
      }

      // Apply non-zero filter
      if (this.showNonZeroOnly) {
        filteredData = filteredData.filter(item =>
          this.valueType === 'absolute'
            ? item.AbsoluteTotal > 0
            : item.RelativeTotal > 0
        )
      }

      return filteredData
    },

    paginatedData () {
      let sortedData = [...this.filteredData]

      // Apply sorting only if not in 'default' state
      if (this.sortDirection !== 'default' && this.sortKey) {
        sortedData.sort((a, b) => {
          let valueA, valueB

          if (this.sortKey === 'LocationName') {
            valueA = a[this.sortKey]
            valueB = b[this.sortKey]
          } else if (this.sortKey === 'AbsoluteTotal') {
            valueA = this.valueType === 'absolute' ? a.AbsoluteTotal : a.RelativeTotal
            valueB = this.valueType === 'absolute' ? b.AbsoluteTotal : b.RelativeTotal
          } else {
            // Sort by date column
            const dateIndex = this.last10Dates.indexOf(this.sortKey)
            valueA = dateIndex !== -1 ? (this.valueType === 'absolute' ? a.last10Days[dateIndex].AbsoluteAlarms : a.last10Days[dateIndex].RelativeAlarms) : 0
            valueB = dateIndex !== -1 ? (this.valueType === 'absolute' ? b.last10Days[dateIndex].AbsoluteAlarms : b.last10Days[dateIndex].RelativeAlarms) : 0
          }

          // Handle string vs number sorting
          if (typeof valueA === 'string') {
            return this.sortDirection === 'asc'
              ? valueA.localeCompare(valueB)
              : valueB.localeCompare(valueA)
          }

          return this.sortDirection === 'asc'
            ? valueA - valueB
            : valueB - valueA
        })
      }

      const start = (this.currentPage - 1) * this.rowsPerPage
      const end = start + this.rowsPerPage
      return sortedData.slice(start, end)
    },

    totalPages () {
      return Math.max(1, Math.ceil(this.filteredData.length / this.rowsPerPage))
    }
  },
  watch: {
    companyId: {
      immediate: true,
      async handler (newCompanyId) {
        if (newCompanyId) {
          // Also clears search query when company id has changed
          await this.refreshData(newCompanyId)
        } else {
          this.processedData = []
          this.searchItems = {}
        }
      }
    },

    searchQuery (newVal) {
      if (!newVal) {
        this.showSuggestions = false
      } else if (!this.isSelecting) {
        this.showSuggestions = true
      }
      this.selectedIndex = -1
      this.updateTable()
    },

    filteredSuggestions () {
      if (this.selectedIndex >= this.filteredSuggestions.length) {
        this.selectedIndex = this.filteredSuggestions.length - 1
      } else if (this.selectedIndex < -1) {
        this.selectedIndex = -1
      }
    },

    valueType () {
      this.processAlarmData()
      this.currentPage = 1
    },

    showNonZeroOnly () {
      this.currentPage = 1
    }
  },
  methods: {
    async fetchIncidents (companyId) {
      this.loading = true
      this.errorMessage = null

      try {
        const [incidentsResponse, searchResponse] = await Promise.all([
          DashboardService.getIncidents(this.currentUser.UserId, companyId),
          DashboardService.getIncidentsSearchItems(this.currentUser.UserId, companyId)
        ])
        this.incidents = incidentsResponse.data
        this.searchItems = searchResponse.data
        this.processAlarmData()
      } catch (error) {
        if (error.response && error.response.data && error.response.data.error) {
          this.errorMessage = 'Failed to load data: ' + error.response.data.error
        } else {
          this.errorMessage = error.message || 'Failed to load data. Please try again.'
        }
        this.incidents = []
        this.searchItems = {}
      }

      this.loading = false
      this.$nextTick(() => {
        this.updateRowsPerPage()
      })
    },

    processAlarmData () {
      if (!this.incidents || this.incidents.length === 0) {
        this.processedData = []
        return
      }

      // Extract all unique dates
      const allDates = [...new Set(this.incidents.flatMap(loc => loc.Day.map(d => d.Date)))]

      // Get the last 10 dates and reverse so most recent date is shown first
      this.last10Dates = allDates.slice(-10).sort((a, b) => a.localeCompare(b)).reverse()

      // Calculate max values based on current valueType
      this.maxErrorsPerDate = this.last10Dates.map(date =>
        Math.max(...this.incidents.map(loc => {
          const day = loc.Day.find(d => d.Date === date) || { AbsoluteAlarms: 0, RelativeAlarms: 0 }
          return this.valueType === 'absolute' ? day.AbsoluteAlarms : day.RelativeAlarms
        }))
      )

      // Max total errors per row
      this.maxTotal = Math.max(...this.incidents.map(loc =>
        this.valueType === 'absolute' ? loc.AbsoluteTotal : loc.RelativeTotal
      ))

      this.processedData = this.incidents.map((location) => {
        // Match LocationId and extract ComplexName
        const matchingRailObject = this.railObjects.find(ro => ro.ComplexId === location.LocationId)
        const complexName = matchingRailObject ? matchingRailObject.ComplexName : `Unknown (${location.LocationId})`

        const last10Days = this.last10Dates.map(date => {
          const day = location.Day.find(d => d.Date === date) || {
            Date: date,
            AbsoluteAlarms: 0,
            RelativeAlarms: 0
          }
          return {
            ...day,
            displayAlarms: this.valueType === 'absolute' ? day.AbsoluteAlarms : day.RelativeAlarms
          }
        })

        return {
          LocationId: location.LocationId,
          LocationName: complexName,
          AbsoluteTotal: location.AbsoluteTotal || 0,
          RelativeTotal: location.RelativeTotal || 0,
          displayTotal: this.valueType === 'absolute' ? location.AbsoluteTotal : location.RelativeTotal,
          last10Days
        }
      })
    },

    sort (key) {
      if (this.sortKey === key) {
        // Cycle through states: asc -> desc -> default
        if (this.sortDirection === 'asc') {
          this.sortDirection = 'desc'
        } else if (this.sortDirection === 'desc') {
          this.sortDirection = 'default'
        } else {
          this.sortDirection = 'asc'
        }
      } else {
        // New column clicked, start with ascending
        this.sortKey = key
        this.sortDirection = 'asc'
      }
      this.currentPage = 1
    },

    formatDate (dateStr) {
      return `${dateStr.substring(6, 8)}-${dateStr.substring(4, 6)}`
    },

    getColor (value, maxValue) {
      if (value === 0) return ''
      if (value < 0) return 'bg-gray'
      if (!maxValue || maxValue === 0) return ''

      const div = maxValue / 3

      if (Math.abs(value - maxValue) < 0.0001) return 'bg-red'
      if (value >= (maxValue - div)) return 'bg-orange'
      if (value >= div) return 'bg-yellow'

      return ''
    },

    formatValue (value) {
      if (this.valueType === 'relative') {
        return value === -1 ? 'N/A' : `${(value * 100).toFixed(1)}%`
      }
      return value
    },

    nextPage () {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },

    prevPage () {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },

    updateRowsPerPage () {
      this.$nextTick(() => {
        const container = this.$el.querySelector('.table-container')
        const tableWrapper = this.$el.querySelector('.table-wrapper')
        const tableHeader = this.$el.querySelector('.incidents-table thead')

        if (container && tableWrapper && tableHeader) {
          const rowHeight = 32 // Row height
          const headerHeight = tableHeader.clientHeight || 40 // Header height
          const paginationHeight = 43 // Pagination controls

          const availableHeight = tableWrapper.clientHeight - headerHeight - paginationHeight

          this.rowsPerPage = Math.max(1, Math.floor(availableHeight / rowHeight))

          if (this.currentPage > this.totalPages) {
            this.currentPage = Math.max(1, this.totalPages)
          }
        }
      })
    },

    async refreshData () {
      if (this.companyId) {
        this.currentPage = 1
        this.sortKey = ''
        this.sortDirection = 'default'
        this.selectedSearchItem = ''
        this.searchQuery = ''
        this.showSuggestions = false
        this.selectedIndex = -1
        this.$emit('updating')
        await this.fetchIncidents(this.companyId)
      }
    },

    clearSearchClick () {
      this.selectedSearchItem = ''
      this.searchQuery = ''
      this.showSuggestions = false
      this.selectedIndex = -1
      this.isSelecting = false
      this.updateTable()
    },

    selectSuggestion (suggestion) {
      this.isSelecting = true
      this.selectedSearchItem = suggestion
      this.searchQuery = suggestion
      this.showSuggestions = false
      this.selectedIndex = -1
      this.$nextTick(() => {
        this.isSelecting = false
      })
      this.updateTable()
    },

    moveSelection (delta) {
      if (!this.showSuggestions || this.filteredSuggestions.length === 0) return

      const newIndex = this.selectedIndex + delta
      if (newIndex >= 0 && newIndex < this.filteredSuggestions.length) {
        this.selectedIndex = newIndex
      } else if (newIndex < 0) {
        this.selectedIndex = this.filteredSuggestions.length - 1
      } else if (newIndex >= this.filteredSuggestions.length) {
        this.selectedIndex = 0
      }
    },

    selectHighlightedSuggestion () {
      this.isSelecting = true
      if (this.searchQuery.trim() === '') {
        this.selectedSearchItem = ''
        this.showSuggestions = false
        this.selectedIndex = -1
        this.isSelecting = false
        this.updateTable()
      } else if (this.selectedIndex >= 0 && this.selectedIndex < this.filteredSuggestions.length) {
        this.selectSuggestion(this.filteredSuggestions[this.selectedIndex])
      } else if (this.filteredSuggestions.length > 0) {
        this.selectSuggestion(this.filteredSuggestions[0])
      }
      this.$nextTick(() => {
        this.showSuggestions = false
      })
    },

    onInput () {
      if (!this.isSelecting && this.searchQuery) {
        this.showSuggestions = true
      }
    },

    handleClickOutside (event) {
      const autocompleteContainer = this.$el.querySelector('.autocomplete-container')
      if (autocompleteContainer && !autocompleteContainer.contains(event.target)) {
        this.showSuggestions = false
        this.selectedIndex = -1
      }
    },

    updateTable () {
      this.currentPage = 1
    },

    async downloadData () {
      if (this.downloading) return

      this.downloading = true
      try {
        const response = await DashboardService.getIncidentsExport(
          this.currentUser.UserId,
          this.companyId,
          { responseType: 'blob' }
        )

        const baseFilename = 'incidents_export'
        const timestamp = new Date().toISOString().replace(/[:.-]/g, '').slice(0, 14)
        const companyIdOrName = this.companyName || this.companyId || 'Unknown'
        const filename = `${baseFilename}_${companyIdOrName}_${timestamp}.xlsx`

        const url = window.URL.createObjectURL(new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.errorMessage = 'Failed to download the file'
      } finally {
        this.downloading = false
      }
    }
  }
}
</script>

<style scoped>
.content-wrapper {
  position: relative;
  min-height: 300px;
  display: flex;
  flex-direction: column;
}

.table-container {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.table-wrapper {
  flex-grow: 1;
  overflow-y: auto;
}

.pagination {
  position: sticky;
  bottom: 4px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 0;
  height: 24px;
  box-sizing: border-box;
  z-index: 1;
}

.pagination-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  line-height: 24px;
  transition: color 0.3s ease;
}

.pagination-btn:disabled {
  opacity: 0.5;
}

.pagination-btn .material-icons {
  font-size: 24px;
  color: #666;
}

.pagination-btn:hover:not(:disabled) .material-icons {
  color: #3498db;
}

.incidents-table {
  width: 100%;
  border-collapse: collapse;
}

.incidents-table th,
.incidents-table td {
  border: 1px solid #ccc;
  padding: 5px;
  text-align: center;
}

.incidents-table td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis; /* Add "..." when text overflows */
  max-width: 200px;
}

.incidents-table th {
  background-color: #f4f4f4;
  height: 41px;
  cursor: pointer;
  user-select: none;
}

.incidents-table th:hover {
  background-color: #e0e0e0;
}

.spinner-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.spinner {
  border: 6px solid #f3f3f3;
  border-top: 6px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-message {
  display: flex;
  align-items: center;
  background-color: rgba(255, 0, 0, 0.1);
  color: #d32f2f;
  border: 1px solid #d32f2f;
  padding: 10px;
  border-radius: 5px;
  font-size: 16px;
  font-weight: 500;
  margin: 10px;
  width: fit-content;
  max-width: 100%;
  text-align: center;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  animation: fadeIn 0.5s ease-in-out;
}

.error-icon {
  margin-right: 8px;
  font-size: 20px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.bg-red {
  background-color: rgba(255, 0, 0, 0.432);
}

.bg-orange {
  background-color: rgba(255, 140, 0, 0.4);
}

.bg-yellow {
  background-color: rgba(240, 240, 0, 0.35);
}

.bg-gray {
  background-color: rgba(128, 128, 128, 0.3);
}

/* Controls Styles */
.controls {
  position: sticky;
  top: 0;
  z-index: 1000;
  background: white;
  padding: 5px;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-size: 12px;
  height: 34px;
  flex-wrap: nowrap;
}

.left-controls {
  display: flex;
  gap: 5px;
  align-items: center;
}

.radio-group {
  display: flex;
  gap: 10px;
  align-items: center;
}

.radio-label, .checkbox-label {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.radio-label span, .checkbox-label span {
  font-size: 12px;
  color: #333;
}

.radio-label input, .checkbox-label input {
  margin: 0;
  width: 14px;
  height: 14px;
  position: relative;
  z-index: 1;
}

.right-controls {
  display: flex;
  gap: 5px;
  align-items: center;
  position: relative;
}

.controls .refresh-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  height: 24px;
  line-height: 24px;
}

.controls .refresh-button .material-icons {
  font-size: 20px;
  color: #666;
}

.controls .refresh-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.autocomplete-container {
  position: relative;
}

.autocomplete-input {
  padding: 2px 4px;
  border: 1px solid #ccc;
  border-radius: 3px;
  font-size: 12px;
  height: 24px;
  width: 175px;
  box-sizing: border-box;
}

.suggestions-list {
  position: absolute;
  top: 28px;
  left: 0;
  width: 175px;
  max-height: 150px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ccc;
  border-radius: 3px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  list-style: none;
  padding: 0;
  margin: 0;
  z-index: 1001;
}

.suggestion-item {
  padding: 2px 4px;
  cursor: pointer;
  font-size: 12px;
  color: #333;
}

.suggestion-item.highlighted {
  background-color: #f4f4f4;
}

.suggestion-item:hover {
  background-color: #e0e0e0;
}
</style>



// WEBPACK FOOTER //
// src/pages/dashboard2/_Incidents.vue